import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { Spinner } from 'reactstrap';
import { timelineActionTypes } from '@store/Timeline/Timeline.actionTypes';
import { useMemberCompanyTimezones } from '@hooks/useMemberCompanyTimezones';
import styles from './AppUsage.module.scss';
import { formatTimeFromSeconds } from '@/utils/timeUtils';
import { normalizeIdleForDisplay } from '@/utils/idleDisplayUtils';
import { ReportFiltersBar } from '@/components/ReportFiltersBar';
import { Table } from '@/components/Table';
import { appUsageTableConfig } from './AppUsage.config';
import { useMembersOptionsForSelect } from '@/hooks/useMembersOptionsForSelect';
import { useEffectiveProductivityApps } from '@/hooks/useEffectiveProductivityApps';
import { GaugeScoreCard } from '@/components/GaugeScoreCard';
import { TrendStatCard } from '@/components/TrendStatCard';
import { SessionAccordionCard } from '@/components/SessionAccordionCard';
import { getProductivityStatus } from '@/utils/productivityStatus';
import {
  useUrlDateRangeSync,
  useUrlMemberHydrationSync,
  useUrlMemberNavSync,
  useUrlTimezoneIgnoreFlagSync,
  useUrlFilterWrite,
  getDefaultMemberFromOptions,
} from '@/hooks/useUrlFilterSync';
import {
  getInitialReportDateRangeFromSearch,
  getReportFiltersFromSearch,
} from '@/utils/reportFiltersUrl';
import { fetchApplicationInfoMapping } from '@services/applicationInfo';
import { getAppNameByAppPath } from '@/utils/utility';
import { EmptyState } from '@/components/EmptyState';
import { IS_DEMO_MODE } from '@/utils/demoMode';
import {
  DEMO_SESSIONS_BY_DAY,
  DEMO_MEMBERS_OPTIONS,
  DEMO_FOCUS_APPS,
  DEMO_DISTRACTION_APPS,
} from './AppUsage.demoData';

export const AppUsageComponent = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { sessionsByDay, loading, filters } = useSelector(
    (state) => state.timeline
  );
  const [selectedMember, setSelectedMember] = useState(null);

  const team = useSelector((state) => state.team);
  const auth = useSelector((state) => state.auth);
  // Get teamId and companyId for timezone hook
  const teamId = team?.active?.activeTeamid;
  const companyId = auth?.profile?.activeCompany?.companyId;
  const memberId = selectedMember?.value || null;
  const {
    membersOptions,
    isLoading: membersLoading,
    isError: membersError,
    error: membersErrorObj,
    refetch: refetchMembers,
  } = useMembersOptionsForSelect(teamId);

  const membersOptionsRef = useRef(membersOptions);
  membersOptionsRef.current = membersOptions;

  const [range, setRange] = useState(() =>
    getInitialReportDateRangeFromSearch(location.search)
  );

  const [ignoreUrlTimezone, setIgnoreUrlTimezone] = useState(false);
  const userOverrideTimezoneRef = useRef(false);

  useUrlDateRangeSync({ location, setDateRange: setRange });
  useUrlMemberHydrationSync({
    location,
    enabled: Boolean(teamId),
    membersOptions,
    selectedMember,
    setSelectedMember,
    currentUserId: auth?.profile?.id,
    getDefaultMember: getDefaultMemberFromOptions,
  });
  useUrlMemberNavSync({
    location,
    enabled: Boolean(teamId),
    membersOptionsRef,
    setSelectedMember,
  });
  useUrlTimezoneIgnoreFlagSync({ history, setIgnoreUrlTimezone });

  const filtersFromUrl = useMemo(
    () => getReportFiltersFromSearch(location.search),
    [location.search]
  );
  const urlTimezoneForHook = useMemo(
    () => (!ignoreUrlTimezone && filtersFromUrl.tz ? filtersFromUrl.tz : null),
    [ignoreUrlTimezone, filtersFromUrl.tz]
  );

  const {
    timezoneOptions,
    selectedTimezone,
    setSelectedTimezone,
    timezonesLoading,
  } = useMemberCompanyTimezones({
    teamId,
    memberId,
    companyId,
    initialTimezone: urlTimezoneForHook || filters?.timeZone || null,
    autoSelectMemberTimezone: true,
    userOverrideTimezoneRef,
  });

  const writeParams = useMemo(() => {
    const params = { start: range.start, end: range.end };
    if (selectedMember?.value) params.member = selectedMember.value;
    if (selectedTimezone) params.tz = selectedTimezone;
    return params;
  }, [range.start, range.end, selectedMember?.value, selectedTimezone]);

  useUrlFilterWrite({
    history,
    location,
    params: writeParams,
    enabled: Boolean(teamId && range?.start && range?.end),
  });

  const onMemberFilterChange = useCallback(
    (opt) => {
      userOverrideTimezoneRef.current = false;
      setIgnoreUrlTimezone(true);
      setSelectedMember(opt);
    },
    [setSelectedMember]
  );

  const handleTimezoneFilterChange = useCallback(
    (value) => {
      userOverrideTimezoneRef.current = true;
      setIgnoreUrlTimezone(true);
      setSelectedTimezone(value);
    },
    [setSelectedTimezone, setIgnoreUrlTimezone, userOverrideTimezoneRef]
  );

  const [appNameMapping, setAppNameMapping] = useState({});
  const { focusApps, distractionApps } = useEffectiveProductivityApps({
    companyId,
    teamId,
  });

  const getEmployeeIdForUser = (userId) => {
    if (!userId) return '';
    const match = membersOptions.find((m) => m.value === userId);
    if (match?.employeeId) return match.employeeId;
    try {
      const stored = JSON.parse(localStorage.getItem('members')) || [];
      const found = stored.find((m) => m.userUID === userId);
      if (found?.employeeId) return found.employeeId;
    } catch (e) {
      console.warn('Unable to parse members from localStorage', e);
    }
    return userId;
  };

  const getRateForUser = (userId) => {
    const match = membersOptions.find((m) => m.value === userId);
    return match?.rate || 0;
  };

  const fetchTimeline = () => {
    if (!selectedMember) return;
    const employeeId = getEmployeeIdForUser(selectedMember.value);
    dispatch({
      type: timelineActionTypes.GET_SESSIONS,
      payload: {
        teamId,
        userUID: selectedMember.value,
        companyId,
        employeeId,
        memberId: selectedMember.value,
        startDate: range.start,
        endDate: range.end,
        timeZone: selectedTimezone,
        page: 1,
        pageSize: 1000, // Large page size to get all sessions
        rate: getRateForUser(selectedMember.value),
      },
    });
  };

  useEffect(() => {
    fetchApplicationInfoMapping().then((mapping) => {
      setAppNameMapping(mapping);
    });
  }, []);

  useEffect(() => {
    if (selectedMember) {
      fetchTimeline();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMember, selectedTimezone, range.start, range.end]);

  // Process app usage for a session
  const getSessionAppUsage = useCallback(
    (session) => {
      // Use appUsage directly from session (available for both split and non-split sessions)
      const appUsage = session.appUsage || {};

      if (!appUsage || Object.keys(appUsage).length === 0) {
        return [];
      }

      return Object.entries(appUsage)
        .map(([appPath, seconds]) => ({
          appPath,
          appName: getAppNameByAppPath(appPath, appNameMapping),
          seconds: Number(seconds) || 0,
          formattedTime: formatTimeFromSeconds(Number(seconds) || 0),
        }))
        .filter((app) => app.seconds > 0) // Filter out zero time
        .sort((a, b) => b.seconds - a.seconds); // Sort by time descending
    },
    [appNameMapping]
  );

  const displaySessionsByDay = IS_DEMO_MODE
    ? DEMO_SESSIONS_BY_DAY
    : sessionsByDay;
  const displayLoading = IS_DEMO_MODE ? false : loading;
  const displayMembersOptions = IS_DEMO_MODE
    ? DEMO_MEMBERS_OPTIONS
    : membersOptions;
  const displaySelectedMember = IS_DEMO_MODE
    ? selectedMember || DEMO_MEMBERS_OPTIONS[0]
    : selectedMember;
  const displayFocusApps = IS_DEMO_MODE ? DEMO_FOCUS_APPS : focusApps;
  const displayDistractionApps = IS_DEMO_MODE
    ? DEMO_DISTRACTION_APPS
    : distractionApps;

  // A single date is selected, so at most one day bucket comes back.
  const day = displaySessionsByDay?.[0];
  const dayTotals = day?.totals;
  const { displayIdle } = normalizeIdleForDisplay(
    dayTotals?.durationSec ?? 0,
    dayTotals?.idleSec ?? 0
  );
  const productivityStatus = getProductivityStatus(dayTotals?.activityPct ?? 0);

  return (
    <div className={styles.appUsagePage}>
      <ReportFiltersBar
        membersOptions={displayMembersOptions}
        selectedMember={displaySelectedMember}
        onMemberFilterChange={onMemberFilterChange}
        membersLoading={membersLoading}
        membersError={membersError}
        membersErrorObj={membersErrorObj}
        refetchMembers={refetchMembers}
        timezoneOptions={timezoneOptions}
        selectedTimezone={selectedTimezone}
        onTimezoneChange={handleTimezoneFilterChange}
        timezonesLoading={timezonesLoading}
        date={range.start}
        onDateChange={(dateStr) => setRange({ start: dateStr, end: dateStr })}
        onRefresh={fetchTimeline}
        showExport={false}
      />

      {displayLoading && (
        <div className={styles.loaderContainer}>
          <Spinner color="primary" />
          <p className="mt-3 text-muted">Loading sessions...</p>
        </div>
      )}

      {!displayLoading && !day && (
        <EmptyState
          illustration
          title="No Sessions Found"
          description="No app usage sessions found for the selected filters."
        />
      )}

      {!displayLoading && day && (
        <>
          <div className={styles.statsRow}>
            <GaugeScoreCard
              score={Number((dayTotals.activityPct ?? 0).toFixed(1))}
              label={productivityStatus.label}
              subtext={productivityStatus.subtext}
            />
            <TrendStatCard
              title="Total Time Tracked"
              tooltip="Total time tracked across all sessions today."
              value={formatTimeFromSeconds(dayTotals.durationSec)}
              tint="success-50"
              sparklineColor="success-500"
              // Placeholder — no hourly time-series exists in the data model
              // yet, so this sparkline is decorative, not real trend data.
              sparklineData={[4, 6, 5, 7, 6, 8, 7]}
            />
            <TrendStatCard
              title="Idle / Rest Time"
              tooltip="Time spent idle across all sessions today."
              value={formatTimeFromSeconds(displayIdle)}
              tint="error-50"
              sparklineColor="error-500"
              // Placeholder — see note above.
              sparklineData={[7, 6, 7, 5, 6, 4, 5]}
            />
          </div>

          <div className={styles.sessionsList}>
            {day.sessions.map((session, index) => {
              const appUsageList = getSessionAppUsage(session);
              const sessionTotalSeconds = appUsageList.reduce(
                (sum, app) => sum + app.seconds,
                0
              );

              return (
                <SessionAccordionCard
                  key={session.id}
                  session={session}
                  index={index}
                  selectedTimezone={selectedTimezone}
                >
                  <Table
                    className={styles.appUsageTable}
                    data={appUsageList}
                    columns={appUsageTableConfig({
                      sessionTotalSeconds,
                      focusApps: displayFocusApps,
                      distractionApps: displayDistractionApps,
                    })}
                  />
                </SessionAccordionCard>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
